import noop from 'lodash/noop';
import { Modal } from 'components/Modal';
import { ModalBody } from 'components/Modal/ModalBody';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ModalActions } from 'components/Modal/ModalActions';
import { Button } from 'components/Button';
import { ArrowLeftLongIcon } from 'icons/outline';
import { Box } from 'components/Box';
import map from 'lodash/map';
import { UserProfileResource } from 'util/resources/UserProfile';
import head from 'lodash/head';
import { LocationResource } from 'util/resources/Location';
import { JobListingResource } from 'util/resources/JobListing';
import filter from 'lodash/filter';
import compact from 'lodash/compact';
import { HiringUpdateFlowContext } from 'features/EmployerHiringUpdateFlow/EmployerHiringUpdateContext';
import { HiringUpdateFlowSteps } from 'features/EmployerHiringUpdateFlow/EmployerHiringUpdateFlow.constants';
import find from 'lodash/find';
import { InputInstructions } from 'features/EmployerHiringUpdateFlow/InputInstructions';
import { HiredUser } from 'features/EmployerHiringUpdateFlow/HiredUser';
import { SelectJobListings } from 'features/EmployerHiringUpdateFlow/SelectJobListings';
import { useEmployerJobListings } from 'hooks/useEmployerJobListings';
import { Skeleton } from 'components/Skeleton';
import { useEmployerLocations } from 'hooks/redux/useEmployerLocations';

export const HiredMultipleTeachersToWhichRoleStep = () => {
  const {
    open,
    step,
    userProfile: singleUserProfile,
    goToNextStep,
    goToPreviousStep,
    hiredApplicants,
    hiredNonApplicantName,
    hiredUserListings,
    setHiredUserListings,
  } = useContext(HiringUpdateFlowContext);

  const { jobs: jobListings, loading } = useEmployerJobListings({
    includes: ['locations'],
  });
  const locations = useEmployerLocations();

  const hiredApplicantsPrimitive = map(hiredApplicants, 'id').join(',');

  const userProfiles = useMemo(
    () =>
      compact([
        ...hiredApplicants,
        hiredNonApplicantName
          ? new UserProfileResource({
              id: undefined,
              firstName: hiredNonApplicantName,
            })
          : null,
      ]),
    [hiredApplicantsPrimitive, hiredNonApplicantName],
  );

  const userProfilesPrimitive = map(userProfiles, 'id').join(',');

  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfileResource>(null);

  useEffect(() => {
    setCurrentUserProfile(head(userProfiles));
  }, [userProfilesPrimitive]);

  const [roleTitle, setRoleTitle] = useState('');
  const [location, setLocation] = useState<LocationResource>(null);
  const [jobListing, setJobListing] = useState<JobListingResource>(null);

  const [showOtherTextField, setShowOtherTextField] = useState(false);

  const [readyForNextStep, setReadyForNextStep] = useState(false);

  const handleSelectJobListing = (value: string) => {
    if (!value) {
      setShowOtherTextField(false);
      setJobListing(null);
      return;
    }

    if (value.toLowerCase() === 'other') {
      setShowOtherTextField(true);
      setJobListing(null);
      return;
    }

    const selectedJobListing = jobListings.find((jobListing) => jobListing.id === value);
    setJobListing(selectedJobListing);
  };

  const handleSelectLocation = (value: string) => {
    const selectedLocation = locations.find((location) => location.id === value);
    setLocation(selectedLocation);
  };

  const handleSaveListingWithoutUser = () => {
    const nonCurrentUserListings = filter(
      hiredUserListings,
      (item) => item.userProfileId !== singleUserProfile?.id,
    );

    const newHiredUserListings = [
      ...nonCurrentUserListings,
      {
        userProfileId: singleUserProfile?.id || undefined,
        userProfileName: undefined,
        jobListingId: jobListing?.id || undefined,
        roleTitle: jobListing?.id ? undefined : roleTitle,
        locationId: jobListing?.id ? undefined : location?.id,
      },
    ];

    setHiredUserListings(newHiredUserListings);

    setReadyForNextStep(true);
  };

  const handleSaveCurrentUser = () => {
    const nonCurrentUserListings = filter(
      hiredUserListings,
      (item) => item.userProfileId !== currentUserProfile.id,
    );

    const newHiredUserListings = [
      ...nonCurrentUserListings,
      {
        userProfileId: currentUserProfile.id || undefined,
        userProfileName: currentUserProfile.id ? undefined : currentUserProfile.fullName(),
        jobListingId: jobListing?.id || undefined,
        roleTitle: jobListing?.id ? undefined : roleTitle,
        locationId: jobListing?.id ? undefined : location?.id,
      },
    ];

    setHiredUserListings(newHiredUserListings);

    const pendingUserProfiles = filter(
      newHiredUserListings,
      (hiredUserListing) =>
        !(
          hiredUserListing.jobListingId ||
          (hiredUserListing.roleTitle && hiredUserListing.locationId)
        ),
    );

    const nextUserProfileId = head(pendingUserProfiles)?.userProfileId;
    const nextUserProfile = !pendingUserProfiles?.length
      ? null
      : find(userProfiles, (userProfile) => userProfile.id === nextUserProfileId);

    setCurrentUserProfile(nextUserProfile);

    setShowOtherTextField(false);
    setJobListing(null);
    setRoleTitle('');
    setLocation(null);

    if (!nextUserProfile) {
      setReadyForNextStep(true);
    } else {
      setReadyForNextStep(false);
    }
  };

  useEffect(() => {
    if (
      open &&
      step === HiringUpdateFlowSteps.HIRED_FOR_ROLES &&
      !currentUserProfile &&
      readyForNextStep
    ) {
      goToNextStep();
      setReadyForNextStep(false);
    }
  }, [open, step, readyForNextStep, currentUserProfile]);

  if (!currentUserProfile) {
    return (
      <Modal
        key="no-user-profile"
        panelProps={{
          className: 'sm:!max-w-[456px]',
        }}
        size="sm"
        open={open && step === HiringUpdateFlowSteps.HIRED_FOR_ROLES}
        heading={
          <span className="text-primary-pure-blue">What role did you hire this person for?</span>
        }
        onClose={noop}
        actionRequired
      >
        <ModalBody>
          <Box className="!pt-[18px] !pb-6 !px-4 mt-4">
            {loading ? (
              <div className="flex flex-col gap-3" data-testid="loading-skeleton">
                <div className="w-full flex items-center gap-2 py-2">
                  <Skeleton className="h-4 w-4 flex-none rounded-full" />
                  <div>
                    <Skeleton className="h-3 w-64 rounded-full flex-none" />
                    <Skeleton className="h-3 w-48 rounded-full flex-none" />
                  </div>
                </div>
                <div className="w-full flex items-center gap-2 py-2">
                  <Skeleton className="h-4 w-4 flex-none rounded-full" />
                  <div>
                    <Skeleton className="h-3 w-48 rounded-full flex-none" />
                    <Skeleton className="h-3 w-36 rounded-full flex-none" />
                  </div>
                </div>
                <div className="w-full flex items-center gap-2 py-2">
                  <Skeleton className="h-4 w-4 flex-none rounded-full" />
                  <div>
                    <Skeleton className="h-3 w-56 rounded-full flex-none" />
                    <Skeleton className="h-3 w-28 rounded-full flex-none" />
                  </div>
                </div>
              </div>
            ) : (
              <SelectJobListings
                selectedJobListing={jobListing}
                jobListings={jobListings}
                locations={locations}
                onSelectionChange={handleSelectJobListing}
                onOtherChange={setRoleTitle}
                selectedLocation={location}
                onLocationChange={handleSelectLocation}
              />
            )}
          </Box>
        </ModalBody>

        <ModalActions>
          <Button
            type="button"
            size="lg"
            fullWidth
            onClick={handleSaveListingWithoutUser}
            disabled={
              (showOtherTextField && (!roleTitle || !location?.id)) ||
              (!showOtherTextField && !jobListing?.id)
            }
          >
            Confirm
          </Button>
          <Button type="button" size="lg" variant="outline" onClick={goToPreviousStep} fullWidth>
            <ArrowLeftLongIcon className="mr-2" />
            <span>Go back</span>
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  return (
    <>
      {map(userProfiles, (userProfile) => (
        <Modal
          key={`multiple-${userProfile.id}`}
          panelProps={{
            className: 'sm:!max-w-[456px]',
          }}
          size="sm"
          open={
            open &&
            step === HiringUpdateFlowSteps.HIRED_FOR_ROLES &&
            currentUserProfile?.id === userProfile.id
          }
          heading={<span className="text-primary-pure-blue">New hire reported! 😉</span>}
          onClose={noop}
          actionRequired
        >
          <ModalBody>
            <InputInstructions>What role did you hire this person for?</InputInstructions>

            <HiredUser userProfile={userProfile} />

            <Box className="!pt-[18px] !pb-6 !px-4 mt-4">
              {loading ? (
                <div className="flex flex-col gap-3" data-testid="loading-skeleton">
                  <div className="w-full flex items-center gap-2 py-2">
                    <Skeleton className="h-4 w-4 flex-none rounded-full" />
                    <div>
                      <Skeleton className="h-3 w-64 rounded-full flex-none" />
                      <Skeleton className="h-3 w-48 rounded-full flex-none" />
                    </div>
                  </div>
                  <div className="w-full flex items-center gap-2 py-2">
                    <Skeleton className="h-4 w-4 flex-none rounded-full" />
                    <div>
                      <Skeleton className="h-3 w-48 rounded-full flex-none" />
                      <Skeleton className="h-3 w-36 rounded-full flex-none" />
                    </div>
                  </div>
                  <div className="w-full flex items-center gap-2 py-2">
                    <Skeleton className="h-4 w-4 flex-none rounded-full" />
                    <div>
                      <Skeleton className="h-3 w-56 rounded-full flex-none" />
                      <Skeleton className="h-3 w-28 rounded-full flex-none" />
                    </div>
                  </div>
                </div>
              ) : (
                <SelectJobListings
                  selectedJobListing={jobListing}
                  jobListings={jobListings}
                  locations={locations}
                  onSelectionChange={handleSelectJobListing}
                  onOtherChange={setRoleTitle}
                  selectedLocation={location}
                  onLocationChange={handleSelectLocation}
                />
              )}
            </Box>
          </ModalBody>

          <ModalActions>
            <Button
              type="button"
              size="lg"
              fullWidth
              onClick={handleSaveCurrentUser}
              disabled={
                (showOtherTextField && (!roleTitle || !location?.id)) ||
                (!showOtherTextField && !jobListing?.id)
              }
            >
              Confirm
            </Button>
            <Button type="button" size="lg" variant="outline" onClick={goToPreviousStep} fullWidth>
              <ArrowLeftLongIcon className="mr-2" />
              <span>Go back</span>
            </Button>
          </ModalActions>
        </Modal>
      ))}
    </>
  );
};
